"use client";

import { useEffect, useRef, useState } from "react";
import {
  archiveGoal,
  deleteGoal,
  replaceGoalMilestones,
  restoreGoal,
  type GoalAnalysis,
  type GoalFocusPriority,
  type GoalItem,
  type GoalMode,
} from "@/lib/api";
import { describeStoredGoalExecutionStyle } from "@/lib/goalCadence";
import { GOAL_WEIGHT_OPTIONS, goalWeightLevel } from "@/lib/goalWeights";
import { sanitizeSurfaceTitle } from "@/lib/savedResourceTitles";
import { LIFE_AREA_KEYS, LIFE_AREA_META } from "@/lib/lifeAreas";
import GoalShapeToggle from "./GoalShapeToggle";
import GoalFocusToggle from "./GoalFocusToggle";

interface Props {
  goal: GoalItem;
  insight?: GoalAnalysis;
  activePanel?: "analysis" | "roadmap" | "judge" | null;
  loading?: boolean;
  actionLoading?: string | null;
  actionMessage?: string;
  onSelectPanel: (goalId: string, panel: "analysis" | "roadmap" | "judge") => void;
  onApplyRoadmap: () => void;
  onStrengthenPlan: () => void;
  onTimeboxGoal: () => void;
  onUpdateWeight?: (
    goalId: string,
    field: "importance_weight" | "willingness_weight",
    value: number
  ) => void;
  // U22 — Goal shape + per-area focus priority. Optional so existing
  // callers (older Screens that don't pass these yet) keep working.
  onUpdateMode?: (goalId: string, mode: GoalMode) => Promise<void> | void;
  onUpdateFocusPriority?: (
    goalId: string,
    priority: GoalFocusPriority
  ) => Promise<void> | void;
  // Other active goals in the same Life Area — used by the focus toggle to
  // preview cascade demotions ("Promoting will move X to Secondary").
  peersInArea?: GoalItem[];
  // U22 — this goal's Layer-1 shape (Phase 2 emits one entry per active
  // goal). Optional so older mounts still work; when present we render a
  // "This week" summary line that finally makes the AI's intent visible.
  weeklyShape?: {
    template_kind: "deep_work" | "habit_box" | "exploration";
    target_minutes: number;
    session_count: number;
    preferred_session_minutes: number;
    reason: string;
  } | null;
  onToggleMilestone?: (goalId: string, milestoneTitle: string) => void;
  // Fires after the user saves a wholesale milestone edit so the
  // parent (GoalsScreen) can splice the fresh GoalItem back into its
  // list without a full refetch.
  onMilestonesReplaced?: (goal: GoalItem) => void;
  // Fires when this goal is archived (soft) so the parent can
  // splice the new (status="abandoned") GoalItem into its list.
  onGoalArchived?: (goal: GoalItem) => void;
  // Fires when this goal is restored from archived/completed back
  // to active so the parent can re-route it through the active
  // list and reattach drift signals.
  onGoalRestored?: (goal: GoalItem) => void;
  // Fires when this goal is hard-deleted so the parent can drop
  // it from the list entirely.
  onGoalDeleted?: (goalId: string) => void;
  onOpenRoadmapModal?: () => void;
  highlighted?: boolean;
  weightUpdateLoading?: string | null;
}

// Per-category chip meta. Pulled from the canonical life-area registry
// so the goal-card category chip stays in sync with PetalLifeWheel,
// BalanceList, QueuePeek, etc. See @/lib/lifeAreas.
const CATEGORY_META: Record<string, { emoji: string; tone: string }> = Object.fromEntries(
  LIFE_AREA_KEYS.map((k) => [
    k,
    { emoji: LIFE_AREA_META[k].emoji, tone: LIFE_AREA_META[k].badge },
  ]),
);

export default function GoalCard({
  goal,
  insight,
  activePanel,
  loading = false,
  actionLoading = null,
  actionMessage,
  onSelectPanel,
  onApplyRoadmap,
  onStrengthenPlan,
  onTimeboxGoal,
  onUpdateWeight,
  onUpdateMode,
  onUpdateFocusPriority,
  peersInArea = [],
  weeklyShape = null,
  onToggleMilestone,
  onMilestonesReplaced,
  onGoalArchived,
  onGoalRestored,
  onGoalDeleted,
  onOpenRoadmapModal,
  highlighted = false,
  weightUpdateLoading = null,
}: Props) {
  const [milestonesExpanded, setMilestonesExpanded] = useState(false);
  // ⋮ menu for archive / delete actions on the goal itself.
  // Only one of {archiving, deleting} can be in flight at a time.
  const [menuOpen, setMenuOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const isArchived = goal.status === "abandoned";
  const isCompleted = goal.status === "completed";
  const menuRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (event: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
        setConfirmingDelete(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [menuOpen]);

  const handleArchive = async () => {
    if (archiving || deleting || restoring) return;
    setArchiving(true);
    try {
      const updated = await archiveGoal(goal.id);
      onGoalArchived?.(updated);
      setMenuOpen(false);
    } catch {
      /* keep menu open so user can retry */
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async (target: "active" | "completed" = "active") => {
    if (archiving || deleting || restoring) return;
    setRestoring(true);
    try {
      const updated = await restoreGoal(goal.id, target);
      onGoalRestored?.(updated);
      setMenuOpen(false);
    } catch {
      /* keep menu open so user can retry */
    } finally {
      setRestoring(false);
    }
  };

  const handleDelete = async () => {
    if (archiving || deleting || restoring) return;
    setDeleting(true);
    try {
      await deleteGoal(goal.id);
      onGoalDeleted?.(goal.id);
      setMenuOpen(false);
      setConfirmingDelete(false);
    } catch {
      /* keep confirm visible so user can retry */
    } finally {
      setDeleting(false);
    }
  };
  // Inline edit mode for the Current Path. The user can rename / add /
  // delete / reorder steps and Save sends the full ordered list to the
  // server in one PUT call.
  const [editingMilestones, setEditingMilestones] = useState(false);
  const [draftMilestones, setDraftMilestones] = useState<
    Array<{ title: string; completed: boolean }>
  >([]);
  const [savingMilestones, setSavingMilestones] = useState(false);
  const [milestoneEditError, setMilestoneEditError] = useState<string | null>(null);

  const startEditingMilestones = () => {
    setDraftMilestones(
      goal.milestones.map((m) => ({
        title: m.title,
        completed: m.completed,
      })),
    );
    setMilestoneEditError(null);
    setEditingMilestones(true);
    setMilestonesExpanded(true);
  };

  const cancelEditingMilestones = () => {
    setEditingMilestones(false);
    setDraftMilestones([]);
    setMilestoneEditError(null);
  };

  const updateDraftTitle = (index: number, value: string) => {
    setDraftMilestones((current) =>
      current.map((item, i) => (i === index ? { ...item, title: value } : item)),
    );
  };

  const toggleDraftCompleted = (index: number) => {
    setDraftMilestones((current) =>
      current.map((item, i) =>
        i === index ? { ...item, completed: !item.completed } : item,
      ),
    );
  };

  const removeDraft = (index: number) => {
    setDraftMilestones((current) => current.filter((_, i) => i !== index));
  };

  const moveDraft = (index: number, direction: -1 | 1) => {
    setDraftMilestones((current) => {
      const next = [...current];
      const targetIndex = index + direction;
      if (targetIndex < 0 || targetIndex >= next.length) return next;
      [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
      return next;
    });
  };

  const addDraft = () => {
    setDraftMilestones((current) => [...current, { title: "", completed: false }]);
  };

  const saveMilestoneEdits = async () => {
    if (savingMilestones) return;
    const cleaned = draftMilestones
      .map((item) => ({ title: item.title.trim(), completed: item.completed }))
      .filter((item) => item.title.length > 0);
    if (cleaned.length === 0 && goal.milestones.length > 0) {
      setMilestoneEditError("Add at least one step or cancel to keep the existing list.");
      return;
    }
    setSavingMilestones(true);
    try {
      const updated = await replaceGoalMilestones(goal.id, cleaned);
      onMilestonesReplaced?.(updated);
      setEditingMilestones(false);
      setDraftMilestones([]);
      setMilestoneEditError(null);
    } catch (err) {
      setMilestoneEditError(err instanceof Error ? err.message : "Could not save changes");
    } finally {
      setSavingMilestones(false);
    }
  };

  const pct = Math.round(goal.progress_pct * 100);
  const completedMilestones = goal.milestones.filter((m) => m.completed).length;
  const totalMilestones = goal.milestones.length;
  const nextRoadmapStep = insight?.roadmap.find((step) => step.status !== "done");
  const applyingRoadmap = actionLoading === `${goal.id}:apply`;
  const strengtheningPlan = actionLoading === `${goal.id}:strengthen`;
  const timeboxingGoal = actionLoading === `${goal.id}:timebox`;
  const categoryKey = goal.category || "personal";
  const categoryMeta = CATEGORY_META[categoryKey] || { emoji: "🎯", tone: "bg-secondary text-text-light" };
  const executionStyle = describeStoredGoalExecutionStyle(goal);
  const roadmapReady = Boolean(insight || totalMilestones > 0);
  const roadmapLabel = roadmapReady ? "Open roadmap" : "Generate roadmap";
  const visibleMilestones = milestonesExpanded ? goal.milestones : goal.milestones.slice(0, 3);
  const goalTitle = sanitizeSurfaceTitle(goal.title);
  const statusTone =
    goal.status === "completed"
      ? "bg-accent/15 text-accent"
      : goal.status === "in_progress"
        ? "bg-blue/15 text-blue"
        : "bg-secondary text-text-light";
  const needsAttention = pct < 35 && totalMilestones > 0;
  const importanceLevel = goalWeightLevel(goal.importance_weight);
  const willingnessLevel = goalWeightLevel(goal.willingness_weight);

  return (
    <div
      data-testid={`goal-card-${goal.id}`}
      className={`rounded-[30px] border bg-card p-5 shadow-sm ${
        highlighted
          ? "border-forest/30 ring-2 ring-forest/15"
          : "border-border"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-bg text-lg">
              {categoryMeta.emoji}
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-lg font-semibold text-text">{goalTitle}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted">
                {goal.description || "This goal still needs a clearer roadmap and a repeatable execution rhythm."}
              </p>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide ${categoryMeta.tone}`}>
              {categoryKey}
            </span>
            <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-semibold text-text-light">
              {executionStyle.label}
            </span>
            <span className={`rounded-full px-2.5 py-1 text-[10px] font-semibold ${statusTone}`}>
              {(goal.status ?? "active").replace("_", " ")}
            </span>
            {goal.target_date && (
              <span className="rounded-full border border-border px-2.5 py-1 text-[10px] font-semibold text-text-light">
                {new Date(goal.target_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </span>
            )}
            {needsAttention && (
              <span className="rounded-full bg-amber/15 px-2.5 py-1 text-[10px] font-semibold text-amber">
                Needs momentum
              </span>
            )}
            {highlighted && (
              <span className="rounded-full bg-forest-med px-2.5 py-1 text-[10px] font-semibold text-white">
                Restart target
              </span>
            )}
          </div>
        </div>

        {/* ⋮ menu — archive (soft) and delete (hard). The two
            destructive paths are separated visually because they
            behave differently: archive is recoverable via "Show
            archived" filters; delete is permanent. */}
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => {
              setMenuOpen((prev) => !prev);
              setConfirmingDelete(false);
            }}
            disabled={archiving || deleting}
            className="flex h-8 w-8 items-center justify-center rounded-full text-muted transition-colors hover:bg-secondary hover:text-text disabled:opacity-50"
            aria-label="Goal actions"
            data-testid={`goal-card-menu-${goal.id}`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="5" r="1.6" />
              <circle cx="12" cy="12" r="1.6" />
              <circle cx="12" cy="19" r="1.6" />
            </svg>
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-9 z-20 w-52 rounded-xl border border-border bg-white p-1.5 shadow-lg">
              {/* State machine:
                  - active     → Archive
                  - completed  → Archive (clean up the Completed list)
                                 + Reopen (back to active to redo it)
                  - abandoned  → Restore (back to active)
                  Archive and Restore are inverses on the visibility
                  axis. Reopen is a separate intent ("I want to do
                  this again") that only makes sense for a goal
                  that's been finished. */}
              {isArchived ? (
                <>
                  {/* Two restore paths from archived: back to active
                      (the user wants to keep working on it) or back to
                      completed (it was finished before archiving and
                      they just want it visible in Completed). Without
                      the second option, restoring would silently
                      un-complete a goal that had already been done. */}
                  <button
                    type="button"
                    onClick={() => void handleRestore("active")}
                    disabled={archiving || deleting || restoring}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-forest-med transition-colors hover:bg-sage-pale hover:text-forest disabled:opacity-50"
                    data-testid={`goal-card-restore-${goal.id}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 12a9 9 0 1 0 3-6.7" />
                      <polyline points="3 4 3 10 9 10" />
                    </svg>
                    {restoring ? "Restoring…" : "Restore to active"}
                  </button>
                  <button
                    type="button"
                    onClick={() => void handleRestore("completed")}
                    disabled={archiving || deleting || restoring}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-accent transition-colors hover:bg-accent/10 disabled:opacity-50"
                    data-testid={`goal-card-restore-completed-${goal.id}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Restore as completed
                  </button>
                </>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => void handleArchive()}
                    disabled={archiving || deleting || restoring}
                    className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-text-light transition-colors hover:bg-secondary hover:text-text disabled:opacity-50"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 8v13H3V8" />
                      <path d="M1 3h22v5H1z" />
                      <path d="M10 12h4" />
                    </svg>
                    {archiving ? "Archiving…" : "Archive goal"}
                  </button>
                  {isCompleted && (
                    <button
                      type="button"
                      onClick={() => void handleRestore()}
                      disabled={archiving || deleting || restoring}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-forest-med transition-colors hover:bg-sage-pale hover:text-forest disabled:opacity-50"
                      data-testid={`goal-card-reopen-${goal.id}`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 12a9 9 0 1 0 3-6.7" />
                        <polyline points="3 4 3 10 9 10" />
                      </svg>
                      {restoring ? "Reopening…" : "Reopen goal"}
                    </button>
                  )}
                </>
              )}
              {!confirmingDelete ? (
                <button
                  type="button"
                  onClick={() => setConfirmingDelete(true)}
                  disabled={archiving || deleting}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-rose transition-colors hover:bg-rose/10 disabled:opacity-50"
                  data-testid={`goal-card-delete-${goal.id}`}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                    <path d="M10 11v6M14 11v6" />
                  </svg>
                  Delete goal
                </button>
              ) : (
                <div className="px-3 py-2">
                  <p className="text-[11px] leading-relaxed text-text-light">
                    Delete <span className="font-semibold text-text">{goalTitle}</span>{" "}
                    permanently? Linked tasks survive but lose this goal link.
                  </p>
                  <div className="mt-2 flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => void handleDelete()}
                      disabled={deleting}
                      className="flex-1 rounded-full bg-rose px-3 py-1.5 text-[11px] font-semibold text-white transition-colors hover:bg-rose/90 disabled:opacity-50"
                      data-testid={`goal-card-delete-confirm-${goal.id}`}
                    >
                      {deleting ? "Deleting…" : "Yes, delete"}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmingDelete(false)}
                      disabled={deleting}
                      className="rounded-full border border-border px-3 py-1.5 text-[11px] font-semibold text-text-light transition-colors hover:bg-secondary disabled:opacity-50"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-5">
        <div className="mb-1.5 flex items-center justify-between">
          <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted">Progress</span>
          <span
            className="text-sm font-semibold text-text"
            data-testid={`goal-progress-pct-${goal.id}`}
          >
            {pct}%
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-secondary">
          <div
            className="h-full rounded-full bg-gradient-to-r from-[#A79AF9] to-[#6F59E8] transition-all duration-500"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <div className="mt-5 rounded-[24px] border border-border/80 bg-bg px-4 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
              Roadmap
            </p>
            <p className="mt-1 text-sm text-text">
              {roadmapReady
                ? `${completedMilestones}/${totalMilestones} milestones tracked${
                    nextRoadmapStep ? ` · next: ${sanitizeSurfaceTitle(nextRoadmapStep.title)}` : ""
                  }`
                : "No roadmap yet. Generate one so this goal becomes a path you can actually follow."}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-muted">
              {executionStyle.hint}
            </p>
          </div>
          {roadmapReady && (
            <span className="rounded-full bg-white px-3 py-1 text-[10px] font-semibold text-text-light shadow-sm">
              {completedMilestones}/{Math.max(totalMilestones, 1)} done
            </span>
          )}
        </div>

        <button
          onClick={() => {
            if (onOpenRoadmapModal) {
              onOpenRoadmapModal();
              return;
            }
            onSelectPanel(goal.id, "roadmap");
          }}
          className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-border bg-white px-4 py-3 text-sm font-semibold text-text shadow-sm transition-colors hover:bg-sage-pale"
        >
          <span>✦</span>
          <span>{roadmapLabel}</span>
        </button>
      </div>

      {goal.status === "active" && onUpdateWeight ? (
        <div className="mt-4 rounded-[24px] border border-border/80 bg-bg px-4 py-4">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
              Weekly weighting
            </p>
            <p className="mt-1 text-sm text-text">
              Mark how much this goal matters and how much pull it has right now.
            </p>
            <p className="mt-1 text-xs leading-relaxed text-muted">
              Plan uses this to protect time without forcing heavy blocks onto low-pull goals.
            </p>
          </div>

          <div className="mt-4 space-y-3">
            {(
              [
                ["importance", "importance_weight", importanceLevel, "Importance to life"],
                ["willingness", "willingness_weight", willingnessLevel, "Willingness right now"],
              ] as const
            ).map(([kind, field, activeLevel, title]) => (
              <div key={field}>
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">
                  {title}
                </p>
                <div className="mt-2 flex flex-wrap gap-2">
                  {GOAL_WEIGHT_OPTIONS[kind].map((option) => {
                    const isActive = option.key === activeLevel;
                    const isLoading = weightUpdateLoading === `${goal.id}:${field}`;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        data-testid={`goal-weight-${field}-${goal.id}-${option.key}`}
                        aria-pressed={isActive}
                        disabled={isLoading}
                        onClick={() => onUpdateWeight(goal.id, field, option.value)}
                        className={`rounded-full px-3 py-1.5 text-[10px] font-semibold transition-colors ${
                          isActive
                            ? "bg-forest-med text-white"
                            : "border border-border bg-white text-text-light hover:bg-sage-pale"
                        } ${isLoading ? "cursor-wait opacity-70" : ""}`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* U22 — Goal shape + per-area focus. Sit alongside the existing
                Importance/Willingness controls because they belong to the
                same "how should AI Plan treat this goal?" conversation. */}
            {onUpdateMode ? (
              <GoalShapeToggle
                goalId={goal.id}
                mode={goal.mode ?? "project"}
                onChange={(next) => onUpdateMode(goal.id, next)}
              />
            ) : null}
            {onUpdateFocusPriority ? (
              <GoalFocusToggle
                goal={goal}
                peersInArea={peersInArea}
                onChange={(next) => onUpdateFocusPriority(goal.id, next)}
              />
            ) : null}

            {/* U22 — "This week" summary: the AI's intent for this goal,
                made visible. Source of truth: /api/planning/week-shape. */}
            {weeklyShape ? (
              <div
                data-testid={`goal-week-shape-${goal.id}`}
                className="rounded-2xl border border-forest-med/20 bg-sage-pale/40 px-3 py-2.5"
              >
                <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-forest-med">
                  This week
                </p>
                <p className="mt-1 text-[12px] font-semibold text-text">
                  {weeklyShape.session_count}
                  {" × "}
                  {weeklyShape.preferred_session_minutes} min
                  {" · "}
                  {weeklyShape.template_kind === "deep_work"
                    ? "deep work"
                    : weeklyShape.template_kind === "habit_box"
                    ? "habit"
                    : "exploration"}
                </p>
                <p className="mt-1 text-[11px] leading-relaxed text-text-light">
                  {weeklyShape.reason}
                </p>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {(totalMilestones > 0 || editingMilestones) && (
        <div className="mt-4 space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted">
              Current path
            </p>
            {!editingMilestones ? (
              <button
                type="button"
                onClick={startEditingMilestones}
                className="inline-flex items-center gap-1 rounded-full border border-border/70 bg-white px-2.5 py-1 text-[10px] font-semibold text-text-light transition-colors hover:border-forest/30 hover:text-text"
                aria-label="Edit current path"
              >
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 20h9" />
                  <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
                </svg>
                Edit
              </button>
            ) : (
              <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-forest">
                Editing
              </span>
            )}
          </div>

          {!editingMilestones && (
            <>
              {visibleMilestones.map((milestone, index) => (
                <button
                  key={`${milestone.title}-${index}`}
                  onClick={() => {
                    if (!milestone.completed && onToggleMilestone) {
                      onToggleMilestone(goal.id, milestone.title);
                    }
                  }}
                  disabled={milestone.completed}
                  className="flex w-full items-center gap-3 rounded-2xl border border-border/70 bg-white px-3 py-2.5 text-left transition-colors hover:border-forest/20 disabled:cursor-default"
                >
                  <div
                    className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold ${
                      milestone.completed
                        ? "border-accent bg-accent text-white"
                        : "border-border bg-bg text-muted"
                    }`}
                  >
                    {milestone.completed ? "✓" : index + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm ${milestone.completed ? "text-muted line-through" : "text-text"}`}>
                      {sanitizeSurfaceTitle(milestone.title)}
                    </p>
                  </div>
                </button>
              ))}
              {totalMilestones > 3 && (
                <button
                  onClick={() => setMilestonesExpanded((current) => !current)}
                  className="text-xs font-medium text-forest hover:underline"
                >
                  {milestonesExpanded ? "Show fewer steps" : `Show all ${totalMilestones} steps`}
                </button>
              )}
            </>
          )}

          {editingMilestones && (
            <div className="space-y-2">
              {draftMilestones.map((draft, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 rounded-2xl border border-border/70 bg-white px-2.5 py-2"
                >
                  <button
                    type="button"
                    onClick={() => toggleDraftCompleted(index)}
                    className={`flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full border text-[10px] font-semibold transition-colors ${
                      draft.completed
                        ? "border-accent bg-accent text-white"
                        : "border-border bg-bg text-muted hover:border-forest/40"
                    }`}
                    aria-label={draft.completed ? "Mark not done" : "Mark done"}
                  >
                    {draft.completed ? "✓" : index + 1}
                  </button>
                  <input
                    type="text"
                    value={draft.title}
                    onChange={(event) => updateDraftTitle(index, event.target.value)}
                    placeholder="Step…"
                    className="min-w-0 flex-1 border-0 bg-transparent px-1 py-1 text-sm text-text placeholder-muted focus:outline-none"
                  />
                  <div className="flex shrink-0 items-center gap-0.5">
                    <button
                      type="button"
                      onClick={() => moveDraft(index, -1)}
                      disabled={index === 0}
                      className="flex h-6 w-6 items-center justify-center rounded-full text-muted transition-colors hover:bg-secondary hover:text-text disabled:opacity-30"
                      aria-label="Move up"
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M18 15l-6-6-6 6" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => moveDraft(index, 1)}
                      disabled={index === draftMilestones.length - 1}
                      className="flex h-6 w-6 items-center justify-center rounded-full text-muted transition-colors hover:bg-secondary hover:text-text disabled:opacity-30"
                      aria-label="Move down"
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M6 9l6 6 6-6" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeDraft(index)}
                      className="flex h-6 w-6 items-center justify-center rounded-full text-muted transition-colors hover:bg-rose/10 hover:text-rose"
                      aria-label="Remove step"
                    >
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M18 6L6 18M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addDraft}
                className="flex w-full items-center justify-center gap-1.5 rounded-2xl border border-dashed border-border bg-bg/40 px-3 py-2 text-xs font-medium text-text-light transition-colors hover:border-forest/40 hover:bg-sage-pale/40 hover:text-forest"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M12 5v14M5 12h14" />
                </svg>
                Add step
              </button>

              {milestoneEditError && (
                <p className="text-[11px] text-rose">{milestoneEditError}</p>
              )}

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => void saveMilestoneEdits()}
                  disabled={savingMilestones}
                  className="flex-1 rounded-full bg-forest-med px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-forest disabled:opacity-50"
                >
                  {savingMilestones ? "Saving…" : "Save"}
                </button>
                <button
                  type="button"
                  onClick={cancelEditingMilestones}
                  disabled={savingMilestones}
                  className="rounded-full border border-border bg-white px-4 py-2 text-xs font-semibold text-text-light transition-colors hover:bg-secondary disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <button
          onClick={() => onSelectPanel(goal.id, "analysis")}
          className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
            activePanel === "analysis"
              ? "bg-forest text-white"
              : "border border-border bg-white text-text-light hover:bg-sage-pale"
          }`}
        >
          AI notes
        </button>
        <button
          onClick={() => onSelectPanel(goal.id, "judge")}
          className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${
            activePanel === "judge"
              ? "bg-forest text-white"
              : "border border-border bg-white text-text-light hover:bg-sage-pale"
          }`}
        >
          Refine goal
        </button>
        <button
          onClick={onTimeboxGoal}
          disabled={timeboxingGoal}
          className="rounded-full bg-forest-med px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-forest disabled:opacity-50"
        >
          {timeboxingGoal ? "Scheduling..." : executionStyle.nextActionLabel}
        </button>
      </div>

      {activePanel && (
        <div className="mt-4 rounded-2xl border border-border/80 bg-bg p-4">
          {loading && (
            <div className="space-y-2 animate-pulse">
              <div className="h-3 w-1/3 rounded bg-muted/10" />
              <div className="h-3 w-full rounded bg-muted/5" />
              <div className="h-3 w-2/3 rounded bg-muted/5" />
            </div>
          )}

          {!loading && insight && activePanel === "analysis" && (
            <div>
              <p className="text-sm leading-relaxed text-text">{insight.analysis}</p>
              {nextRoadmapStep && (
                <div className="mt-3 rounded-2xl border border-blue/15 bg-white p-3">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted">Best next move</p>
                  <p className="mt-1 text-sm font-semibold text-text">{sanitizeSurfaceTitle(nextRoadmapStep.title)}</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted">{nextRoadmapStep.reason}</p>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <span className="rounded-full bg-blue/10 px-2.5 py-1 text-[10px] font-medium text-blue">
                  {insight.linked_tasks} linked tasks
                </span>
                <span className="rounded-full bg-amber/10 px-2.5 py-1 text-[10px] font-medium text-amber">
                  {insight.linked_thoughts} supporting thoughts
                </span>
              </div>
            </div>
          )}

          {!loading && insight && activePanel === "judge" && (
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-forest/10 font-semibold text-forest">
                  {insight.verdict.score}
                </div>
                <div>
                  <p className="text-sm font-semibold text-text">{insight.verdict.label}</p>
                  <p className="text-xs text-muted">{insight.verdict.summary}</p>
                </div>
              </div>
              {insight.verdict.gaps.length > 0 && (
                <div className="mt-3 space-y-1.5">
                  {insight.verdict.gaps.map((gap) => (
                    <p key={gap} className="text-xs leading-relaxed text-text-light">
                      {gap}
                    </p>
                  ))}
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={onStrengthenPlan}
                  disabled={strengtheningPlan}
                  className="rounded-full bg-forest px-4 py-2 text-xs font-semibold text-white disabled:opacity-50"
                >
                  {strengtheningPlan ? "Refining..." : "Fix weak spots"}
                </button>
                <button
                  onClick={onApplyRoadmap}
                  disabled={applyingRoadmap}
                  className="rounded-full border border-border bg-white px-4 py-2 text-xs font-semibold text-text-light disabled:opacity-50"
                >
                  {applyingRoadmap ? "Saving..." : "Save as current path"}
                </button>
              </div>
            </div>
          )}

          {!loading && !insight && (
            <p className="text-sm leading-relaxed text-muted">
              Ask AI to analyze this goal first so it can generate a better roadmap and next move.
            </p>
          )}

          {actionMessage && (
            <div className="mt-3 rounded-2xl border border-blue/15 bg-blue/10 px-3 py-2.5 text-xs leading-relaxed text-blue">
              {actionMessage}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
